import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useEffect, useState, useRef } from "react";

// Legacy scopes:
// Backend token must target API resource scope, not Graph-only fallback scopes.
const FALLBACK_BACKEND_SCOPE = "https://storage.azure.com/user_impersonation";
const AUTH_TOKEN_STORAGE_KEY = "axon_auth_token";
const AUTH_TOKEN_EXPIRY_STORAGE_KEY = "axon_auth_token_expires_at";
const TOKEN_EXPIRY_BUFFER_MS = 30 * 1000;

const OIDC_SCOPES = new Set(["openid", "profile", "email", "offline_access"]);

const decodeJwtPayload = (token) => {
  try {
    const tokenParts = (token || "").split(".");
    if (tokenParts.length !== 3) {
      return null;
    }

    const payload = tokenParts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(tokenParts[1].length / 4) * 4, "=");

    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

const getConfiguredTokenScopes = () => {
  const configuredScopes = (process.env.REACT_APP_TOKEN_SCOPE || "").trim();

  if (!configuredScopes || configuredScopes === "t") {
    return [];
  }

  return configuredScopes.split(/\s+/).filter(Boolean);
};

const getExpectedAudienceFromScopes = () => {
  const scopes = getBackendTokenScopes();
  const resourceScope = scopes.find((scope) => {
    const normalizedScope = (scope || "").toLowerCase();
    return normalizedScope.includes("/") && !OIDC_SCOPES.has(normalizedScope);
  });

  if (!resourceScope) {
    return "";
  }

  const slashIndex = resourceScope.lastIndexOf("/");
  return slashIndex > 0 ? resourceScope.slice(0, slashIndex) : resourceScope;
};

const isTokenAudienceValid = (token) => {
  const expectedAudience = getExpectedAudienceFromScopes();
  if (!expectedAudience) {
    return true;
  }

  const payload = decodeJwtPayload(token);
  const tokenAudience = payload?.aud;
  const normalizedAudience = Array.isArray(tokenAudience)
    ? tokenAudience[0]
    : tokenAudience;

  return normalizedAudience === expectedAudience;
};

const getBackendTokenScopes = () => {
  const configuredScopes = getConfiguredTokenScopes();

  if (configuredScopes.length > 0) {
    return configuredScopes;
  }

  return [FALLBACK_BACKEND_SCOPE];
};

const clearStoredAuthToken = () => {
  localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY);
};

const getStoredAuthToken = () => {
  const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || "";
  const expiry = Number(
    localStorage.getItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY) || "0",
  );

  if (!token) {
    return { token: "", expiresAt: null };
  }

  if (expiry && Date.now() >= expiry - TOKEN_EXPIRY_BUFFER_MS) {
    clearStoredAuthToken();
    return { token: "", expiresAt: null };
  }

  if (!isTokenAudienceValid(token)) {
    console.warn(
      "[Auth] Ignoring stored token due to audience mismatch. Acquire a new token with REACT_APP_TOKEN_SCOPE.",
    );
    clearStoredAuthToken();
    return { token: "", expiresAt: null };
  }

  return { token, expiresAt: expiry || null };
};

const storeAuthToken = (token, expiresOn) => {
  if (!token) {
    clearStoredAuthToken();
    return;
  }

  if (!isTokenAudienceValid(token)) {
    console.warn(
      "[Auth] Received token audience does not match API scope. Token will not be stored.",
    );
    clearStoredAuthToken();
    return;
  }

  const expiresAt = expiresOn ? new Date(expiresOn).getTime() : null;

  localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);

  if (expiresAt) {
    localStorage.setItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY, expiresAt.toString());
  } else {
    localStorage.removeItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY);
  }
};

const useGetToken = () => {
  const { instance, accounts, inProgress } = useMsal();
  // Legacy behavior:
  // const [accessToken, setAccessToken] = useState("");
  const [accessToken, setAccessToken] = useState(
    () => getStoredAuthToken().token,
  );
  const [tokenExpiresAt, setTokenExpiresAt] = useState(
    () => getStoredAuthToken().expiresAt,
  );
  const isAcquiringToken = useRef(false);

  useEffect(() => {
    if (
      inProgress === "none" &&
      accounts.length > 0 &&
      !isAcquiringToken.current
    ) {
      // Legacy behavior:
      // // Use proper scopes - if REACT_APP_TOKEN_SCOPE is not set or invalid, use defaults
      // const configuredScope = process.env.REACT_APP_TOKEN_SCOPE;
      // const scopes =
      //   configuredScope && configuredScope !== "t" && configuredScope.length > 1
      //     ? [configuredScope]
      //     : DEFAULT_SCOPES;
      const scopes = getBackendTokenScopes();

      const request = {
        scopes: scopes,
        account: accounts[0],
      };

      isAcquiringToken.current = true;

      instance
        .acquireTokenSilent(request)
        .then((response) => {
          setAccessToken(response.accessToken);
          const expiresAt = response.expiresOn
            ? new Date(response.expiresOn).getTime()
            : null;
          setTokenExpiresAt(expiresAt);
          storeAuthToken(response.accessToken, response.expiresOn);
          isAcquiringToken.current = false;
        })
        .catch((error) => {
          console.error("Silent token acquisition failed:", error);
          clearStoredAuthToken();
          setAccessToken("");
          setTokenExpiresAt(null);
          isAcquiringToken.current = false;

          // Only redirect if it's truly an interaction required error and we haven't tried already
          if (error instanceof InteractionRequiredAuthError) {
            // Check if we've already tried consent in this session
            const consentAttempted =
              sessionStorage.getItem("consent_attempted");
            if (!consentAttempted) {
              sessionStorage.setItem("consent_attempted", "true");
              instance
                .acquireTokenRedirect({
                  // Legacy scopes:
                  // scopes: DEFAULT_SCOPES,
                  scopes,
                  prompt: "consent",
                })
                .catch((redirectError) => {
                  console.error("Token redirect failed:", redirectError);
                });
            }
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, accounts]);

  // Clear consent flag when we have a token
  useEffect(() => {
    if (accessToken) {
      sessionStorage.removeItem("consent_attempted");
    }
  }, [accessToken]);

  useEffect(() => {
    if (accounts.length > 0) {
      return;
    }

    clearStoredAuthToken();
    setAccessToken("");
    setTokenExpiresAt(null);
  }, [accounts.length]);

  useEffect(() => {
    if (!accessToken || !tokenExpiresAt) {
      return;
    }

    const timeoutMs = tokenExpiresAt - Date.now() - TOKEN_EXPIRY_BUFFER_MS;

    const expireAndRelogin = () => {
      clearStoredAuthToken();
      setAccessToken("");
      setTokenExpiresAt(null);

      if (inProgress === "none") {
        const scopes = getBackendTokenScopes();
        // Legacy behavior:
        // instance
        //   .loginRedirect({ scopes: DEFAULT_SCOPES })
        //   .catch((redirectError) => {
        //     console.error(
        //       "Login redirect after token expiry failed:",
        //       redirectError,
        //     );
        //   });
        instance.loginRedirect({ scopes }).catch((redirectError) => {
          console.error(
            "Login redirect after token expiry failed:",
            redirectError,
          );
        });
      }
    };

    if (timeoutMs <= 0) {
      expireAndRelogin();
      return;
    }

    const expiryTimer = setTimeout(expireAndRelogin, timeoutMs);
    return () => clearTimeout(expiryTimer);
  }, [accessToken, tokenExpiresAt, inProgress, instance]);

  return accessToken;
};

const useGetTokenForImage = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [accessToken, setAccessToken] = useState("");
  const isAcquiringToken = useRef(false);

  useEffect(() => {
    if (
      inProgress === "none" &&
      accounts.length > 0 &&
      !isAcquiringToken.current
    ) {
      const request = {
        scopes: ["User.Read"],
        account: accounts[0],
      };

      isAcquiringToken.current = true;

      instance
        .acquireTokenSilent(request)
        .then((response) => {
          setAccessToken(response.accessToken);
          isAcquiringToken.current = false;
        })
        .catch((error) => {
          console.error("Silent token acquisition for image failed:", error);
          isAcquiringToken.current = false;
          // Don't redirect for image token - just log the error
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, accounts]);

  return accessToken;
};

export { useGetToken, useGetTokenForImage };
